import {
  Row,
  TableKeys,
  GetColumns,
  QuarterKey,
  RowWithDate,
  PriorValues
} from '../types';
import {
  DEPENDENCIES,
  COMMA_OPTIONS,
  INT_TABLE_KEYS,
  COMMA_TABLE_KEYS
} from './constants';
import { calculateCellValue } from './calculateCellValue';
import { formatCalculationResult } from 'utils/helpers/calculateFunctions';

export const getColumns: GetColumns = (title, labels) => [
  {
    header: title,
    accessorKey: 'title',
    size: 260,
    minSize: 260,
    meta: { editable: false }
  },
  ...labels.map(l => ({
    header: l.replace('q', 'Q').replace('_', ' '),
    accessorKey: l,
    minSize: 90
  }))
];

export const mapFormulas = (
  data: Row[],
  value: number | null,
  selectedRowId: TableKeys,
  selectedColId: QuarterKey,
  dependents: Record<TableKeys, TableKeys[]>,
  labels: QuarterKey[],
  isEndingArrUpdated: boolean,
  isValueNull?: boolean
): Row[] => {
  const colIdx = labels.findIndex(i => i === selectedColId);

  const values = Object.fromEntries(
    data.map(d => {
      const cellValue = d[selectedColId]?.replace(/,/g, '') || null;

      return [
        d.id,
        cellValue === '' || cellValue === null ? null : Number(cellValue)
      ];
    })
  ) as Record<TableKeys, number | null>;

  values[selectedRowId] = value;

  const res: Row[] = data.map(row => {
    const rowId = row.id as TableKeys;
    let newValue: string | null = row[selectedColId] || null;

    if (
      isEndingArrUpdated &&
      row.id === TableKeys.YOY_ARR_GROWTH &&
      colIdx < labels.length - 4
    ) {
      const nextYearValues = Object.fromEntries(
        data.map(d => {
          const cellValue = d[labels[colIdx + 4]]?.replace(/,/g, '') || null;

          return [
            d.id,
            cellValue === '' || cellValue === null ? null : Number(cellValue)
          ];
        })
      ) as Record<TableKeys, number>;

      const lastYearEndingArr =
        data.find(d => d.id === TableKeys.ENDING_ARR)?.[labels[colIdx - 4]] ===
          null ||
        data.find(d => d.id === TableKeys.ENDING_ARR)?.[labels[colIdx - 4]] ===
          ''
          ? null
          : Number(
              data.find(d => d.id === TableKeys.ENDING_ARR)?.[
                labels[colIdx - 4]
              ] || 0
            );

      return {
        ...row,
        [selectedColId]: calculateCellValue(row.id, values, {
          last_year_ending_arr: lastYearEndingArr
        }),
        [labels[colIdx + 4]]: calculateCellValue(row.id, nextYearValues, {
          last_year_ending_arr: values.ending_arr
        })
      };
    }

    if (
      selectedRowId === TableKeys.NEW_LOGO_ARR &&
      row.id === TableKeys.NEW_LOGO_VELOCITY &&
      colIdx < labels.length - 4
    ) {
      const nextYearValues = Object.fromEntries(
        data.map(d => {
          const cellValue = d[labels[colIdx + 4]]?.replace(/,/g, '') || null;

          return [
            d.id,
            cellValue === '' || cellValue === null ? null : Number(cellValue)
          ];
        })
      ) as Record<TableKeys, number>;

      const lastYearNewLogoArr =
        data.find(d => d.id === TableKeys.NEW_LOGO_ARR)?.[
          labels[colIdx - 4]
        ] === null ||
        data.find(d => d.id === TableKeys.NEW_LOGO_ARR)?.[
          labels[colIdx - 4]
        ] === ''
          ? null
          : Number(
              data.find(d => d.id === TableKeys.NEW_LOGO_ARR)?.[
                labels[colIdx - 4]
              ] || 0
            );

      return {
        ...row,
        [selectedColId]: calculateCellValue(row.id, values, {
          [TableKeys.NEW_LOGO_ARR]: lastYearNewLogoArr
        }),
        [labels[colIdx + 4]]: calculateCellValue(row.id, nextYearValues, {
          [TableKeys.NEW_LOGO_ARR]: values.new_logo_arr
        })
      };
    }

    if (
      selectedRowId === TableKeys.TOTAL_OPEX_SM &&
      row.id === TableKeys.NET_MAGIC_NUMBER &&
      colIdx > 0 &&
      colIdx < labels.length - 1
    ) {
      const nextQuarterValues = Object.fromEntries(
        data.map(d => {
          const cellValue = d[labels[colIdx + 1]]?.replace(/,/g, '') || null;

          return [
            d.id,
            cellValue === '' || cellValue === null ? null : Number(cellValue)
          ];
        })
      ) as Record<TableKeys, number>;

      const lastYearTotalOpexSm =
        data.find(d => d.id === TableKeys.TOTAL_OPEX_SM)?.[
          labels[colIdx - 1]
        ] === null ||
        data.find(d => d.id === TableKeys.TOTAL_OPEX_SM)?.[
          labels[colIdx - 1]
        ] === ''
          ? null
          : Number(
              data.find(d => d.id === TableKeys.TOTAL_OPEX_SM)?.[
                labels[colIdx - 1]
              ] || 0
            );

      return {
        ...row,
        [selectedColId]: calculateCellValue(row.id, values, {
          [TableKeys.TOTAL_OPEX_SM]: lastYearTotalOpexSm
        }),
        [labels[colIdx + 1]]: calculateCellValue(row.id, nextQuarterValues, {
          [TableKeys.TOTAL_OPEX_SM]: values.total_opex_sm
        })
      };
    }

    if (
      (selectedRowId === TableKeys.FCF_MARGIN &&
        row.id === TableKeys.FCF_MARGIN_1) ||
      (selectedRowId === TableKeys.GROSS_PROFIT_MARGIN &&
        row.id === TableKeys.GROSS_PROFIT_MARGIN_1)
    ) {
      return {
        ...row,
        [selectedColId]: value === null ? null : value.toFixed(3)
      };
    }

    if (row.id === selectedRowId) {
      newValue =
        value === null || value === undefined || isValueNull
          ? null
          : INT_TABLE_KEYS.includes(selectedRowId) &&
            COMMA_TABLE_KEYS.includes(selectedRowId)
          ? value.toLocaleString()
          : COMMA_TABLE_KEYS.includes(selectedRowId)
          ? value.toLocaleString('en-US', COMMA_OPTIONS)
          : INT_TABLE_KEYS.includes(selectedRowId)
          ? value.toFixed(0)
          : value.toFixed(3);
    } else if (dependents[selectedRowId].includes(rowId)) {
      const priorValues: PriorValues = {
        [TableKeys.ENDING_ARR]:
          data.find(d => d.id === TableKeys.ENDING_ARR)?.[
            labels[colIdx - 1]
          ] === null ||
          data.find(d => d.id === TableKeys.ENDING_ARR)?.[
            labels[colIdx - 1]
          ] === ''
            ? null
            : Number(
                data.find(d => d.id === TableKeys.ENDING_ARR)?.[
                  labels[colIdx - 1]
                ]
              ), // Prior quarter
        last_year_ending_arr:
          data.find(d => d.id === TableKeys.ENDING_ARR)?.[
            labels[colIdx - 4]
          ] === null ||
          data.find(d => d.id === TableKeys.ENDING_ARR)?.[
            labels[colIdx - 4]
          ] === ''
            ? null
            : Number(
                data.find(d => d.id === TableKeys.ENDING_ARR)?.[
                  labels[colIdx - 4]
                ]
              ), // Prior year
        [TableKeys.NEW_LOGO_ARR]:
          data.find(d => d.id === TableKeys.NEW_LOGO_ARR)?.[
            labels[colIdx - 4]
          ] === null ||
          data.find(d => d.id === TableKeys.NEW_LOGO_ARR)?.[
            labels[colIdx - 4]
          ] === ''
            ? null
            : Number(
                data.find(d => d.id === TableKeys.NEW_LOGO_ARR)?.[
                  labels[colIdx - 4]
                ]
              ), // Prior year
        [TableKeys.TOTAL_OPEX_SM]:
          data.find(d => d.id === TableKeys.TOTAL_OPEX_SM)?.[
            labels[colIdx - 1]
          ] === null ||
          data.find(d => d.id === TableKeys.TOTAL_OPEX_SM)?.[
            labels[colIdx - 1]
          ] === ''
            ? null
            : Number(
                data.find(d => d.id === TableKeys.TOTAL_OPEX_SM)?.[
                  labels[colIdx - 1]
                ]
              ) // Prior quarter
      };

      newValue = calculateCellValue(rowId, values, priorValues);
      values[rowId] =
        newValue === null || newValue === '' || newValue === undefined
          ? null
          : Number(newValue.replace(/,/g, ''));
    }

    return { ...row, [selectedColId]: newValue };
  });

  const nextQuarterBopArr = data.find(d => d.id === TableKeys.BOP_ARR)?.[
    labels[colIdx + 1]
  ];

  const isEmptyBopArr =
    nextQuarterBopArr === '0.000' ||
    nextQuarterBopArr === null ||
    nextQuarterBopArr === '';

  if (
    isEndingArrUpdated &&
    colIdx < labels.length - 1 &&
    (values.ending_arr !== null ||
      (values.ending_arr === null && isEmptyBopArr))
  ) {
    return mapFormulas(
      res,
      values.ending_arr === null ? null : values.ending_arr,
      TableKeys.BOP_ARR,
      labels[colIdx + 1],
      dependents,
      labels,
      true,
      values.ending_arr === null
    );
  }

  return res;
};

export const getDependents = (
  field: TableKeys,
  depMap: typeof DEPENDENCIES
): Set<TableKeys> => {
  const result = new Set<TableKeys>();

  const visit = (key: TableKeys) => {
    for (const [dependent, deps] of Object.entries(depMap)) {
      if (deps?.includes(key)) {
        result.add(dependent as TableKeys);
        visit(dependent as TableKeys);
      }
    }
  };

  visit(field);

  return result;
};

export const calculateDivision = (
  numerator: number | null,
  denominator: number | null,
  isPercent: boolean = false,
  isComparison: boolean = false,
  isRounded: boolean = true,
  decimalPlaces: number = 5
): number | null => {
  if (denominator === 0) {
    return null;
  }

  if (!numerator || !denominator) {
    return 0;
  }

  const result =
    (numerator / denominator - (isComparison ? 1 : 0)) * (isPercent ? 100 : 1);
  return Number(formatCalculationResult(result, decimalPlaces, isRounded));
};

export const sortDataByQuarter = <T extends RowWithDate>(data: T[]): T[] => {
  return data.sort((a, b) => {
    const [qa, ya] = a.QUARTER.split(' ');
    const [qb, yb] = b.QUARTER.split(' ');

    const yearA = parseInt(ya, 10);
    const yearB = parseInt(yb, 10);

    if (yearA !== yearB) {
      return yearA - yearB;
    }

    const quarterA = parseInt(qa.replace('Q', ''), 10);
    const quarterB = parseInt(qb.replace('Q', ''), 10);

    return quarterA - quarterB;
  });
};

export const roundToDecimal = (
  value: number,
  decimalPlaces: number = 3
): string => {
  const factor = Math.pow(10, decimalPlaces);
  return (Math.round(value * factor) / factor).toFixed(decimalPlaces);
};
